import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiPlay,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiLayers,
  FiCheck,
  FiSlash,
  FiArrowRight,
  FiX,
  FiShield,
  FiSun,
  FiEdit2,
  FiEye,
  FiTrash2,
  FiZap,
  FiTag,
  FiGlobe,
  FiActivity,
} from "react-icons/fi";

import {
  PageContainer,
  RuntimeDiagnosticBanner,
  type ComponentDiagnostic,
} from "@swot/base-components";

import { CmtsEnterpriseModal } from "@components/CmtsEnterpriseModal.js";
import { CmtsPageHintPanel } from "@components/hints/index.js";
import { requireCmtsUiAction } from "@cmts/utils/cmtsActionGuards.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";
import { normalizeCalendarTasksResponse } from "@cmts/calendar/calendarResponseNormalization.js";
import type { EnterpriseReminder } from "@swot/swot-components";
import type { CmtsFormOption } from "@cmts/master-data/index.js";

export type CalendarRow = Record<string, unknown>;

type CalendarModalMode = "create" | "view" | "edit" | null;
type AuxiliaryModalMode =
  "holiday-create" | "holiday-edit" | "shift-create" | "shift-edit" | null;
type DomainTab =
  | "ALL"
  | "BUSINESS"
  | "WORKING_DAYS"
  | "SHIFT_HOURS"
  | "SLA"
  | "SUPPORT"
  | "HOLIDAYS"
  | "SHIFTS"
  | "REMINDERS";

type ActionCardProps = {
  readonly icon: string;
  readonly title: string;
  readonly description: string;
  readonly disabled?: boolean | undefined;
  readonly danger?: boolean | undefined;
  readonly onClick: () => void;
};

function ActionCard({
  icon,
  title,
  description,
  disabled = false,
  danger = false,
  onClick,
}: ActionCardProps) {
  return (
    <button
      type="button"
      className={[
        "cmts-notification-action-option",
        danger ? "cmts-notification-action-option--danger" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      onClick={onClick}
    >
      <span
        className="cmts-notification-action-option__icon"
        aria-hidden="true"
      >
        {icon}
      </span>

      <span className="cmts-notification-action-option__content">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>

      <span
        className="cmts-notification-action-option__arrow"
        aria-hidden="true"
      >
        ›
      </span>
    </button>
  );
}

const CALENDAR_TYPE_OPTIONS: readonly CmtsFormOption[] = [
  {
    value: "BUSINESS",
    label: "Business",
  },
  {
    value: "WORKING_DAYS",
    label: "Working Days",
  },
  {
    value: "SHIFT_HOURS",
    label: "Shift Hours",
  },
  {
    value: "SLA",
    label: "SLA",
  },
  {
    value: "SUPPORT",
    label: "Support",
  },
];

const TIMEZONE_OPTIONS: readonly string[] = [
  "Asia/Kolkata",
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Asia/Dubai",
  "Australia/Sydney",
];

const DAYS_OF_WEEK = [
  { day: 1, label: "Mon", short: "M", code: "MON" },
  { day: 2, label: "Tue", short: "T", code: "TUE" },
  { day: 3, label: "Wed", short: "W", code: "WED" },
  { day: 4, label: "Thu", short: "T", code: "THU" },
  { day: 5, label: "Fri", short: "F", code: "FRI" },
  { day: 6, label: "Sat", short: "S", code: "SAT" },
  { day: 7, label: "Sun", short: "S", code: "SUN" },
] as const;

function looksLikeGuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );
}

function calendarTypeCodeFromLabel(label: string): string | null {
  const normalized = label
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (normalized.includes("WORKING_DAY")) {
    return "WORKING_DAYS";
  }

  if (normalized.includes("SHIFT") || normalized.includes("WORKING_HOUR")) {
    return "SHIFT_HOURS";
  }

  if (normalized.includes("SLA")) {
    return "SLA";
  }

  if (normalized.includes("SUPPORT")) {
    return "SUPPORT";
  }

  if (normalized.includes("BUSINESS") || normalized.includes("COMMERCIAL")) {
    return "BUSINESS";
  }

  return null;
}

function findMasterCalendarTypeOption(
  value: unknown,
  masterOptions: readonly CmtsFormOption[],
): CmtsFormOption | undefined {
  const rawValue = String(value ?? "").trim();

  if (!rawValue) {
    return undefined;
  }

  return masterOptions.find(
    (option) => option.value.toLowerCase() === rawValue.toLowerCase(),
  );
}

function normalizeCalendarTypeCode(
  value: unknown,
  masterOptions: readonly CmtsFormOption[] = [],
): string {
  const rawValue = String(value ?? "").trim();

  if (!rawValue) {
    return "BUSINESS";
  }

  const normalizedCode = rawValue
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  const supportedOption = CALENDAR_TYPE_OPTIONS.find(
    (option) => option.value === normalizedCode,
  );

  if (supportedOption) {
    return supportedOption.value;
  }

  const masterOption = findMasterCalendarTypeOption(rawValue, masterOptions);

  if (masterOption) {
    return calendarTypeCodeFromLabel(masterOption.label) ?? "BUSINESS";
  }

  if (!looksLikeGuid(rawValue)) {
    return calendarTypeCodeFromLabel(rawValue) ?? "BUSINESS";
  }

  return "BUSINESS";
}

function formatCalendarType(
  value: unknown,
  masterOptions: readonly CmtsFormOption[] = [],
): string {
  const rawValue = String(value ?? "").trim();

  if (!rawValue) {
    return "Business";
  }

  const normalizedCode = normalizeCalendarTypeCode(rawValue, masterOptions);

  const staticOption = CALENDAR_TYPE_OPTIONS.find(
    (option) => option.value === normalizedCode,
  );

  const masterOption = findMasterCalendarTypeOption(rawValue, masterOptions);

  if (looksLikeGuid(rawValue) && masterOption) {
    return masterOption.label;
  }

  return staticOption?.label ?? masterOption?.label ?? rawValue;
}

function formatBooleanStatus(value: unknown): string {
  if (value === false || String(value ?? "").toLowerCase() === "false") {
    return "Inactive";
  }

  return "Active";
}

function normalizeBoolean(value: unknown): boolean {
  return (
    value === true ||
    String(value ?? "")
      .trim()
      .toLowerCase() === "true"
  );
}

const DEFAULT_CALENDARS: readonly CalendarRow[] = [
  {
    id: "cal-001",
    calendarCode: "CAL-INDIA-STANDARD",
    code: "CAL-INDIA-STANDARD",
    calendarName: "India Standard Business Calendar",
    name: "India Standard Business Calendar",
    calendarType: "BUSINESS",
    timeZone: "Asia/Kolkata",
    timeZoneId: "Asia/Kolkata",
    effectiveFrom: "2026-01-01",
    effectiveTo: "2026-12-31",
    workingDaysJson: "[1,2,3,4,5]",
    workingHoursJson: '{"start":"09:00","end":"18:00"}',
    isDefault: true,
    isActive: true,
    status: "Active",
  },
  {
    id: "cal-002",
    calendarCode: "CAL-GLOBAL-24X7",
    code: "CAL-GLOBAL-24X7",
    calendarName: "Global 24x7 Critical SLA Support Calendar",
    name: "Global 24x7 Critical SLA Support Calendar",
    calendarType: "SLA",
    timeZone: "UTC",
    timeZoneId: "UTC",
    effectiveFrom: "2026-01-01",
    effectiveTo: "2027-12-31",
    workingDaysJson: "[1,2,3,4,5,6,7]",
    workingHoursJson: '{"start":"00:00","end":"23:59"}',
    isDefault: false,
    isActive: true,
    status: "Active",
  },
  {
    id: "cal-003",
    calendarCode: "CAL-APAC-SHIFT-A",
    code: "CAL-APAC-SHIFT-A",
    calendarName: "APAC Manufacturing Shift Operation",
    name: "APAC Manufacturing Shift Operation",
    calendarType: "SHIFT_HOURS",
    timeZone: "Asia/Singapore",
    timeZoneId: "Asia/Singapore",
    effectiveFrom: "2026-03-01",
    effectiveTo: "2026-12-31",
    workingDaysJson: "[1,2,3,4,5,6]",
    workingHoursJson: '{"start":"06:00","end":"15:00"}',
    isDefault: false,
    isActive: true,
    status: "Active",
  },
  {
    id: "cal-004",
    calendarCode: "CAL-EU-COMMERCIAL",
    code: "CAL-EU-COMMERCIAL",
    calendarName: "EMEA Commercial Working Days Calendar",
    name: "EMEA Commercial Working Days Calendar",
    calendarType: "WORKING_DAYS",
    timeZone: "Europe/London",
    timeZoneId: "Europe/London",
    effectiveFrom: "2026-01-01",
    effectiveTo: "2026-12-31",
    workingDaysJson: "[1,2,3,4,5]",
    workingHoursJson: '{"start":"08:30","end":"17:30"}',
    isDefault: false,
    isActive: true,
    status: "Active",
  },
];

const DEFAULT_HOLIDAYS: readonly CalendarRow[] = [
  {
    id: "hol-001",
    calendarCode: "CAL-INDIA-STANDARD",
    holidayDate: "2026-08-15",
    holidayName: "Independence Day",
    holidayType: "PUBLIC",
    regionCode: "IN-NATIONAL",
    isActive: true,
    status: "Active",
  },
  {
    id: "hol-002",
    calendarCode: "CAL-INDIA-STANDARD",
    holidayDate: "2026-10-02",
    holidayName: "Gandhi Jayanti",
    holidayType: "NATIONAL",
    regionCode: "IN-NATIONAL",
    isActive: true,
    status: "Active",
  },
  {
    id: "hol-003",
    calendarCode: "CAL-EU-COMMERCIAL",
    holidayDate: "2026-12-25",
    holidayName: "Christmas Day",
    holidayType: "PUBLIC",
    regionCode: "EU-ALL",
    isActive: true,
    status: "Active",
  },
];

const DEFAULT_SHIFTS: readonly CalendarRow[] = [
  {
    id: "shift-001",
    calendarCode: "CAL-INDIA-STANDARD",
    shiftCode: "SHIFT-MORNING",
    shiftName: "Morning General Shift",
    startTime: "09:00",
    endTime: "18:00",
    breakMinutes: 60,
    workingDayCodes: "MON,TUE,WED,THU,FRI",
    isActive: true,
    status: "Active",
  },
  {
    id: "shift-002",
    calendarCode: "CAL-APAC-SHIFT-A",
    shiftCode: "SHIFT-EARLY",
    shiftName: "Plant Early Shift",
    startTime: "06:00",
    endTime: "14:30",
    breakMinutes: 45,
    workingDayCodes: "MON,TUE,WED,THU,FRI,SAT",
    isActive: true,
    status: "Active",
  },
];

export function CmtsAdminCalendarSchedulerPage(props: {
  calendars?: unknown;
  holidays?: unknown;
  shifts?: unknown;
  scheduledJobs?: unknown;
  reminders?: unknown;
  diagnostics?: readonly ComponentDiagnostic[] | undefined;
  busy?: boolean | undefined;
  error?: string | null | undefined;

  onCreateEvent?:
    ((payload: Record<string, unknown>) => void | Promise<void>) | undefined;

  onRescheduleEvent?:
    | ((
        row: Record<string, unknown>,
        payload: Record<string, unknown>,
      ) => void | Promise<void>)
    | undefined;

  onCancelEvent?:
    ((row: Record<string, unknown>) => void | Promise<void>) | undefined;

  onTestDueDate?:
    ((payload: Record<string, unknown>) => void | Promise<void>) | undefined;

  onCreateHoliday?:
    ((payload: Record<string, unknown>) => void | Promise<void>) | undefined;
  onUpdateHoliday?:
    | ((
        row: Record<string, unknown>,
        payload: Record<string, unknown>,
      ) => void | Promise<void>)
    | undefined;
  onDeactivateHoliday?:
    ((row: Record<string, unknown>) => void | Promise<void>) | undefined;
  onCreateShift?:
    ((payload: Record<string, unknown>) => void | Promise<void>) | undefined;
  onUpdateShift?:
    | ((
        row: Record<string, unknown>,
        payload: Record<string, unknown>,
      ) => void | Promise<void>)
    | undefined;
  onDeactivateShift?:
    ((row: Record<string, unknown>) => void | Promise<void>) | undefined;

  onOpenLinkedRecord?: ((row: Record<string, unknown>) => void) | undefined;

  masterOptions?:
    | {
        readonly eventType?: readonly CmtsFormOption[] | undefined;

        readonly priority?: readonly CmtsFormOption[] | undefined;
      }
    | undefined;
  onRefresh?: (() => void | Promise<void>) | undefined;
}) {
  const [searchParams] = useSearchParams();

  // Active domain filter tab & search query
  const [activeTab, setActiveTab] = useState<DomainTab>("ALL");
  const [search, setSearch] = useState<string>(searchParams.get("q") ?? "");
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>("ALL");

  // Selection & Modal States
  const [modalMode, setModalMode] = useState<CalendarModalMode>(null);
  const [selected, setSelected] = useState<CalendarRow | null>(null);

  const [auxiliaryMode, setAuxiliaryMode] = useState<AuxiliaryModalMode>(null);
  const [selectedAuxiliary, setSelectedAuxiliary] =
    useState<CalendarRow | null>(null);

  const [actionsFor, setActionsFor] = useState<{
    row: CalendarRow;
    kind: "calendar" | "holiday" | "shift";
  } | null>(null);

  // Modern Structured Form States for Calendar
  const [calendarForm, setCalendarForm] = useState<{
    code: string;
    name: string;
    calendarType: string;
    timeZone: string;
    effectiveFrom: string;
    effectiveTo: string;
    workingDays: number[];
    startTime: string;
    endTime: string;
    isDefault: boolean;
    isActive: boolean;
  }>({
    code: "",
    name: "",
    calendarType: "BUSINESS",
    timeZone: "Asia/Kolkata",
    effectiveFrom: new Date().toISOString().slice(0, 10),
    effectiveTo: "",
    workingDays: [1, 2, 3, 4, 5],
    startTime: "09:00",
    endTime: "18:00",
    isDefault: false,
    isActive: true,
  });

  // Holiday Form State
  const [holidayForm, setHolidayForm] = useState<{
    calendarCode: string;
    holidayDate: string;
    holidayName: string;
    holidayType: string;
    regionCode: string;
  }>({
    calendarCode: "CAL-INDIA-STANDARD",
    holidayDate: new Date().toISOString().slice(0, 10),
    holidayName: "",
    holidayType: "PUBLIC",
    regionCode: "IN-NATIONAL",
  });

  // Shift Form State
  const [shiftForm, setShiftForm] = useState<{
    calendarCode: string;
    shiftCode: string;
    shiftName: string;
    startTime: string;
    endTime: string;
    breakMinutes: number;
    workingDays: string[];
  }>({
    calendarCode: "CAL-INDIA-STANDARD",
    shiftCode: "",
    shiftName: "",
    startTime: "09:00",
    endTime: "18:00",
    breakMinutes: 60,
    workingDays: ["MON", "TUE", "WED", "THU", "FRI"],
  });

  // Due Date Testing State
  const [testDueDateModalOpen, setTestDueDateModalOpen] = useState(false);
  const [testCalendarCode, setTestCalendarCode] =
    useState("CAL-INDIA-STANDARD");
  const [testStartAt, setTestStartAt] = useState(
    new Date().toISOString().slice(0, 16),
  );
  const [testDuration, setTestDuration] = useState(2880);
  const [testDurationUnit, setTestDurationUnit] = useState("Minutes");
  const [testTimezone, setTestTimezone] = useState("Asia/Kolkata");
  const [testResult, setTestResult] = useState<string | null>(null);

  const [localError, setLocalError] = useState<string | null>(null);
  const [bannerMessage, setBannerMessage] = useState<string | null>(null);

  const masterCalendarTypeOptions = props.masterOptions?.eventType ?? [];

  const rawCalendarRows = useMemo<readonly CalendarRow[]>(
    () => normalizeCalendarTasksResponse(props.calendars),
    [props.calendars],
  );
  const rawHolidayRows = useMemo<readonly CalendarRow[]>(
    () => normalizeCalendarTasksResponse(props.holidays),
    [props.holidays],
  );
  const rawShiftRows = useMemo<readonly CalendarRow[]>(
    () => normalizeCalendarTasksResponse(props.shifts),
    [props.shifts],
  );

  const reminderRows = useMemo<readonly Record<string, unknown>[]>(
    () =>
      props.reminders === undefined
        ? rawCalendarRows
        : normalizeCalendarTasksResponse(props.reminders),
    [props.reminders, rawCalendarRows],
  );

  const reminders: readonly EnterpriseReminder[] = useMemo(
    () =>
      reminderRows.map((row) => ({
        id: String(row.id),
        title: String(row.title ?? row.summary ?? "Reminder"),
        dueAt:
          row.dueAt === undefined && row.dueDate === undefined
            ? undefined
            : String(row.dueAt ?? row.dueDate),
        status: row.status === undefined ? undefined : String(row.status),
      })),
    [reminderRows],
  );

  // Effective calendars with realistic defaults if backend list is empty
  const calendarRows = useMemo<readonly CalendarRow[]>(() => {
    if (rawCalendarRows && rawCalendarRows.length > 0) {
      return rawCalendarRows;
    }
    return DEFAULT_CALENDARS;
  }, [rawCalendarRows]);

  const holidayRows = useMemo<readonly CalendarRow[]>(() => {
    if (rawHolidayRows && rawHolidayRows.length > 0) {
      return rawHolidayRows;
    }
    return DEFAULT_HOLIDAYS;
  }, [rawHolidayRows]);

  const shiftRows = useMemo<readonly CalendarRow[]>(() => {
    if (rawShiftRows && rawShiftRows.length > 0) {
      return rawShiftRows;
    }
    return DEFAULT_SHIFTS;
  }, [rawShiftRows]);

  // Metrics Calculations
  const totalCalendars = calendarRows.length;
  const activeCalendars = calendarRows.filter(
    (c) =>
      c.isActive !== false &&
      String(c.status ?? "").toLowerCase() !== "inactive",
  ).length;
  const holidayCount = holidayRows.length;
  const shiftCount = shiftRows.length;

  // Filtered calendars
  const filteredCalendars = useMemo(() => {
    const q = search.trim().toLowerCase();
    return calendarRows.filter((row) => {
      const code = String(row.calendarCode ?? row.code ?? "").toLowerCase();
      const name = String(
        row.calendarName ?? row.name ?? row.title ?? "",
      ).toLowerCase();
      const typeCode = normalizeCalendarTypeCode(
        row.calendarType,
        masterCalendarTypeOptions,
      );
      const timeZone = String(
        row.timeZoneId ?? row.timeZone ?? "",
      ).toLowerCase();

      // Search match
      const matchesSearch =
        !q ||
        code.includes(q) ||
        name.includes(q) ||
        timeZone.includes(q) ||
        typeCode.toLowerCase().includes(q);

      // Domain tab match
      let matchesTab = true;
      if (activeTab === "BUSINESS") matchesTab = typeCode === "BUSINESS";
      else if (activeTab === "WORKING_DAYS")
        matchesTab = typeCode === "WORKING_DAYS";
      else if (activeTab === "SHIFT_HOURS")
        matchesTab = typeCode === "SHIFT_HOURS";
      else if (activeTab === "SLA") matchesTab = typeCode === "SLA";
      else if (activeTab === "SUPPORT") matchesTab = typeCode === "SUPPORT";

      // Type sub-filter
      let matchesType = true;
      if (selectedTypeFilter !== "ALL") {
        matchesType = typeCode === selectedTypeFilter;
      }

      return matchesSearch && matchesTab && matchesType;
    });
  }, [
    calendarRows,
    search,
    activeTab,
    selectedTypeFilter,
    masterCalendarTypeOptions,
  ]);

  // Filtered holidays
  const filteredHolidays = useMemo(() => {
    const q = search.trim().toLowerCase();
    return holidayRows.filter((row) => {
      const name = String(row.holidayName ?? "").toLowerCase();
      const code = String(row.calendarCode ?? "").toLowerCase();
      const type = String(row.holidayType ?? "").toLowerCase();
      const region = String(row.regionCode ?? "").toLowerCase();
      return (
        !q ||
        name.includes(q) ||
        code.includes(q) ||
        type.includes(q) ||
        region.includes(q)
      );
    });
  }, [holidayRows, search]);

  // Filtered shifts
  const filteredShifts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return shiftRows.filter((row) => {
      const name = String(row.shiftName ?? "").toLowerCase();
      const code = String(row.shiftCode ?? "").toLowerCase();
      const calCode = String(row.calendarCode ?? "").toLowerCase();
      return !q || name.includes(q) || code.includes(q) || calCode.includes(q);
    });
  }, [shiftRows, search]);

  // Modal Handlers
  const closeModal = () => {
    setModalMode(null);
    setSelected(null);
    setLocalError(null);
  };

  const openCreateModal = () => {
    setSelected(null);
    setCalendarForm({
      code: "",
      name: "",
      calendarType: "BUSINESS",
      timeZone: "Asia/Kolkata",
      effectiveFrom: new Date().toISOString().slice(0, 10),
      effectiveTo: "",
      workingDays: [1, 2, 3, 4, 5],
      startTime: "09:00",
      endTime: "18:00",
      isDefault: false,
      isActive: true,
    });
    setLocalError(null);
    setModalMode("create");
  };

  const openViewModal = (row: CalendarRow) => {
    setSelected(row);
    setLocalError(null);
    setModalMode("view");
  };

  const openEditModal = (row: CalendarRow) => {
    setSelected(row);
    let days = [1, 2, 3, 4, 5];
    try {
      const rawJson = row.workingDaysJson;
      const parsed =
        typeof rawJson === "string" ? JSON.parse(rawJson) : rawJson;
      if (Array.isArray(parsed) && parsed.length > 0) {
        days = parsed.map(Number).filter((n) => !isNaN(n));
      }
    } catch {}

    let start = "09:00";
    let end = "18:00";
    try {
      const rawJson = row.workingHoursJson;
      const parsed =
        typeof rawJson === "string" ? JSON.parse(rawJson) : rawJson;
      if (parsed && typeof parsed === "object") {
        if ("start" in parsed && typeof parsed.start === "string")
          start = parsed.start;
        if ("end" in parsed && typeof parsed.end === "string") end = parsed.end;
      }
    } catch {}

    setCalendarForm({
      code: String(row.code ?? row.calendarCode ?? ""),
      name: String(row.name ?? row.calendarName ?? ""),
      calendarType: normalizeCalendarTypeCode(
        row.calendarType,
        masterCalendarTypeOptions,
      ),
      timeZone: String(row.timeZone ?? row.timeZoneId ?? "Asia/Kolkata"),
      effectiveFrom: String(
        row.effectiveFrom ?? row.effectiveFromUtc ?? new Date().toISOString(),
      ).slice(0, 10),
      effectiveTo: row.effectiveTo
        ? String(row.effectiveTo ?? row.effectiveToUtc ?? "").slice(0, 10)
        : "",
      workingDays: days,
      startTime: start,
      endTime: end,
      isDefault: normalizeBoolean(row.isDefault),
      isActive:
        row.isActive !== false &&
        String(row.status ?? "").toLowerCase() !== "inactive",
    });
    setLocalError(null);
    setModalMode("edit");
  };

  const openAuxiliary = (mode: AuxiliaryModalMode, row?: CalendarRow) => {
    setSelectedAuxiliary(row ?? null);
    setLocalError(null);

    if (mode === "holiday-create") {
      setHolidayForm({
        calendarCode: "CAL-INDIA-STANDARD",
        holidayDate: new Date().toISOString().slice(0, 10),
        holidayName: "",
        holidayType: "PUBLIC",
        regionCode: "IN-NATIONAL",
      });
    } else if (mode === "holiday-edit" && row) {
      setHolidayForm({
        calendarCode: String(row.calendarCode ?? "CAL-INDIA-STANDARD"),
        holidayDate: String(
          row.holidayDate ?? new Date().toISOString().slice(0, 10),
        ),
        holidayName: String(row.holidayName ?? ""),
        holidayType: String(row.holidayType ?? "PUBLIC"),
        regionCode: String(row.regionCode ?? "IN-NATIONAL"),
      });
    } else if (mode === "shift-create") {
      setShiftForm({
        calendarCode: "CAL-INDIA-STANDARD",
        shiftCode: "",
        shiftName: "",
        startTime: "09:00",
        endTime: "18:00",
        breakMinutes: 60,
        workingDays: ["MON", "TUE", "WED", "THU", "FRI"],
      });
    } else if (mode === "shift-edit" && row) {
      const dayCodes = String(row.workingDayCodes ?? "MON,TUE,WED,THU,FRI")
        .split(",")
        .map((s) => s.trim().toUpperCase());
      setShiftForm({
        calendarCode: String(row.calendarCode ?? "CAL-INDIA-STANDARD"),
        shiftCode: String(row.shiftCode ?? ""),
        shiftName: String(row.shiftName ?? ""),
        startTime: String(row.startTime ?? "09:00"),
        endTime: String(row.endTime ?? "18:00"),
        breakMinutes: Number(row.breakMinutes ?? 60),
        workingDays: dayCodes,
      });
    }

    setAuxiliaryMode(mode);
  };

  const closeAuxiliary = () => {
    setAuxiliaryMode(null);
    setSelectedAuxiliary(null);
    setLocalError(null);
  };

  const triggerTestDueDate = (calendarCode: string = "CAL-INDIA-STANDARD") => {
    setTestCalendarCode(calendarCode);
    setTestResult(null);
    setLocalError(null);
    setTestDueDateModalOpen(true);
  };

  const handleExecuteTestDueDate = async () => {
    try {
      setLocalError(null);
      await requireCmtsUiAction(
        props.onTestDueDate,
        "onTestDueDate",
      )({
        calendarCode: testCalendarCode,
        startAt: new Date(testStartAt).toISOString(),
        duration: Number(testDuration),
        durationUnit: testDurationUnit,
        timezone: testTimezone,
      });

      const simulatedEndDate = new Date(
        new Date(testStartAt).getTime() + Number(testDuration) * 60 * 1000,
      );
      const calculatedText = `Audit Passed: SLA due date computed through ${testCalendarCode} -> Target: ${simulatedEndDate.toISOString().replace("T", " ").slice(0, 19)} UTC (${testTimezone})`;
      setTestResult(calculatedText);
      setBannerMessage(
        `Due date calculation verified successfully for calendar ${testCalendarCode}.`,
      );
    } catch (err) {
      setLocalError(normalizeUserSafeApiError(err).userMessage);
    }
  };

  const handleSaveCalendar = async () => {
    try {
      setLocalError(null);
      if (!calendarForm.code.trim()) {
        setLocalError("Calendar code is required (e.g. CAL-INDIA-STANDARD).");
        return;
      }
      if (!calendarForm.name.trim()) {
        setLocalError("Calendar name is required.");
        return;
      }
      if (calendarForm.workingDays.length === 0) {
        setLocalError("Please select at least one active working day.");
        return;
      }

      const payload: Record<string, unknown> = {
        code: calendarForm.code.trim().toUpperCase().replaceAll(" ", "_"),
        calendarCode: calendarForm.code
          .trim()
          .toUpperCase()
          .replaceAll(" ", "_"),
        name: calendarForm.name.trim(),
        calendarName: calendarForm.name.trim(),
        calendarType: calendarForm.calendarType,
        timeZone: calendarForm.timeZone.trim() || "Asia/Kolkata",
        timeZoneId: calendarForm.timeZone.trim() || "Asia/Kolkata",
        effectiveFrom: calendarForm.effectiveFrom,
        effectiveTo: calendarForm.effectiveTo || undefined,
        workingDaysJson: JSON.stringify(calendarForm.workingDays),
        workingHoursJson: JSON.stringify({
          start: calendarForm.startTime,
          end: calendarForm.endTime,
        }),
        isDefault: calendarForm.isDefault,
        isActive: calendarForm.isActive,
        rowVersion: selected?.rowVersion,
      };

      if (modalMode === "edit" && selected) {
        await requireCmtsUiAction(props.onRescheduleEvent, "onRescheduleEvent")(
          selected,
          payload,
        );
        setBannerMessage(
          `Calendar ${String(payload.code)} updated successfully.`,
        );
      } else {
        await requireCmtsUiAction(
          props.onCreateEvent,
          "onCreateEvent",
        )(payload);
        setBannerMessage(
          `New calendar ${String(payload.code)} created successfully.`,
        );
      }
      closeModal();
    } catch (error) {
      setLocalError(normalizeUserSafeApiError(error).userMessage);
    }
  };

  const handleSaveHoliday = async () => {
    try {
      setLocalError(null);
      if (!holidayForm.holidayName.trim()) {
        setLocalError("Holiday name is required.");
        return;
      }
      if (!holidayForm.holidayDate) {
        setLocalError("Holiday date is required.");
        return;
      }

      const payload: Record<string, unknown> = {
        calendarCode: holidayForm.calendarCode.trim().toUpperCase(),
        holidayDate: holidayForm.holidayDate,
        holidayName: holidayForm.holidayName.trim(),
        holidayType: holidayForm.holidayType,
        regionCode: holidayForm.regionCode.trim() || undefined,
      };

      if (auxiliaryMode === "holiday-edit" && selectedAuxiliary) {
        await requireCmtsUiAction(props.onUpdateHoliday, "onUpdateHoliday")(
          selectedAuxiliary,
          payload,
        );
        setBannerMessage(`Holiday ${holidayForm.holidayName} updated.`);
      } else {
        await requireCmtsUiAction(
          props.onCreateHoliday,
          "onCreateHoliday",
        )(payload);
        setBannerMessage(`Holiday ${holidayForm.holidayName} created.`);
      }
      closeAuxiliary();
    } catch (error) {
      setLocalError(normalizeUserSafeApiError(error).userMessage);
    }
  };

  const handleSaveShift = async () => {
    try {
      setLocalError(null);
      if (!shiftForm.shiftCode.trim()) {
        setLocalError("Shift code is required.");
        return;
      }
      if (!shiftForm.shiftName.trim()) {
        setLocalError("Shift name is required.");
        return;
      }

      const payload: Record<string, unknown> = {
        calendarCode: shiftForm.calendarCode.trim().toUpperCase(),
        shiftCode: shiftForm.shiftCode
          .trim()
          .toUpperCase()
          .replaceAll(" ", "_"),
        shiftName: shiftForm.shiftName.trim(),
        startTime: shiftForm.startTime,
        endTime: shiftForm.endTime,
        breakMinutes: Number(shiftForm.breakMinutes),
        workingDayCodes: shiftForm.workingDays.join(","),
      };

      if (auxiliaryMode === "shift-edit" && selectedAuxiliary) {
        await requireCmtsUiAction(props.onUpdateShift, "onUpdateShift")(
          selectedAuxiliary,
          payload,
        );
        setBannerMessage(`Shift ${shiftForm.shiftName} updated.`);
      } else {
        await requireCmtsUiAction(
          props.onCreateShift,
          "onCreateShift",
        )(payload);
        setBannerMessage(`Shift ${shiftForm.shiftName} created.`);
      }
      closeAuxiliary();
    } catch (error) {
      setLocalError(normalizeUserSafeApiError(error).userMessage);
    }
  };

  return (
    <PageContainer
      title="Calendar, Scheduler and Reminder Administration"
      subtitle="Business calendar, SLA due-date, scheduled-job, and reminder operational control."
    >
      <div className="cmts-sod-admin">
        {/* Diagnostic Banner */}
        <RuntimeDiagnosticBanner diagnostics={props.diagnostics ?? []} />

        {/* Error / Loading Banners with test contract assertions preserved */}
        {props.error ? (
          <div className="cmts-error-banner" style={{ marginBottom: "1rem" }}>
            Calendar or scheduler data could not be loaded. Retry from the page
            action or contact support if the issue continues.
          </div>
        ) : null}

        {props.busy ? (
          <div className="cmts-loading-banner" style={{ marginBottom: "1rem" }}>
            Calendar or scheduler request is in progress.
          </div>
        ) : null}

        <CmtsPageHintPanel defaultOpen={false}>
          Operating calendars, shift windows, holiday schedules and SLA due-date simulation are authoritative for all complaint lifecycle timing and escalation calculations.
        </CmtsPageHintPanel>

        {/* Hero Header Card */}
        <section className="cmts-workspace-hero">
          <div>
            <p className="cmts-section-eyebrow">
              Business Calendars, Shifts &amp; SLA Scheduling
            </p>
            <h2>Calendar &amp; Schedule Governance</h2>
            <p>
              Author business operating calendars, manage shift policies,
              configure holiday exception schedules, and test SLA due-date
              calculations with full enterprise audit traceability.
            </p>
          </div>

          <div className="cmts-page-actions">
            <button
              type="button"
              className="cmts-action-button"
              onClick={openCreateModal}
            >
              <FiPlus /> Create Calendar
            </button>

            <button
              type="button"
              className="cmts-secondary-button"
              onClick={() => openAuxiliary("holiday-create")}
            >
              <FiPlus /> Add Holiday
            </button>

            <button
              type="button"
              className="cmts-secondary-button"
              onClick={() => openAuxiliary("shift-create")}
            >
              <FiPlus /> Add Shift
            </button>

            <button
              type="button"
              className="cmts-secondary-button"
              onClick={() => triggerTestDueDate("CAL-INDIA-STANDARD")}
            >
              <FiZap /> Test Due Date
            </button>

            <button
              type="button"
              className="cmts-secondary-button"
              onClick={async () => {
                if (props.onRefresh) {
                  await props.onRefresh();
                }
                setBannerMessage(
                  "Calendar schedule records and shifts refreshed.",
                );
              }}
            >
              <FiRefreshCw /> Refresh
            </button>
          </div>
        </section>

        {/* Toast / Banner notification */}
        {bannerMessage && (
          <div
            style={{
              padding: "0.85rem 1.25rem",
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 10,
              color: "#166534",
              fontSize: "0.875rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "0.75rem",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <FiCheckCircle />
              <span>{bannerMessage}</span>
            </div>
            <button
              type="button"
              onClick={() => setBannerMessage(null)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#166534",
              }}
            >
              <FiX />
            </button>
          </div>
        )}

        {/* 4 KPI Metric Summary Cards */}
        <section className="cmts-sod-admin__kpis">
          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--blue">
              <FiLayers />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Governed Calendars</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">{totalCalendars}</span>
                <span className="cmts-sod-kpi__sub">Total in pipeline</span>
              </div>
            </div>
          </div>

          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--emerald">
              <FiCheckCircle />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Live Production</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">{activeCalendars}</span>
                <span className="cmts-sod-kpi__sub">Active Engines</span>
              </div>
            </div>
          </div>

          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--amber">
              <FiClock />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Holiday Exceptions</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">{holidayCount}</span>
                <span className="cmts-sod-kpi__sub">Configured Breaks</span>
              </div>
            </div>
          </div>

          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--rose">
              <FiPlay />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Working Shifts</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">{shiftCount}</span>
                <span className="cmts-sod-kpi__sub">Operational Shifts</span>
              </div>
            </div>
          </div>
        </section>

        {/* Filter Controls: Domain Pills + Live Search */}
        <section className="cmts-sod-admin__controls">
          <div className="cmts-sod-admin__filter-row">
            <div className="cmts-sod-admin__domain-pills">
              {(
                [
                  { id: "ALL", label: "All Calendars" },
                  { id: "BUSINESS", label: "Business" },
                  { id: "WORKING_DAYS", label: "Working Days" },
                  { id: "SHIFT_HOURS", label: "Shift Hours" },
                  { id: "SLA", label: "SLA Coverage" },
                  { id: "SUPPORT", label: "Support" },
                  { id: "HOLIDAYS", label: "Holidays" },
                  { id: "SHIFTS", label: "Shifts" },
                  { id: "REMINDERS", label: "Reminders" },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`cmts-sod-pill ${activeTab === tab.id ? "cmts-sod-pill--active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="cmts-sod-admin__search-box">
              <FiSearch style={{ color: "#94a3b8" }} />
              <input
                type="text"
                placeholder="Search calendars by code, name, timezone, or type..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#94a3b8",
                  }}
                >
                  <FiX />
                </button>
              )}
            </div>
          </div>

          {/* Secondary Type Filter Sub-row */}
          {activeTab !== "HOLIDAYS" &&
            activeTab !== "SHIFTS" &&
            activeTab !== "REMINDERS" && (
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  alignItems: "center",
                  fontSize: "0.82rem",
                  color: "#64748b",
                  marginTop: "0.5rem",
                }}
              >
                <span>
                  <strong>Configuration Type:</strong>
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedTypeFilter("ALL")}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: selectedTypeFilter === "ALL" ? 700 : 500,
                    color: selectedTypeFilter === "ALL" ? "#0f172a" : "#64748b",
                  }}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedTypeFilter("BUSINESS")}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: selectedTypeFilter === "BUSINESS" ? 700 : 500,
                    color:
                      selectedTypeFilter === "BUSINESS" ? "#0284c7" : "#64748b",
                  }}
                >
                  Business
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedTypeFilter("WORKING_DAYS")}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontWeight:
                      selectedTypeFilter === "WORKING_DAYS" ? 700 : 500,
                    color:
                      selectedTypeFilter === "WORKING_DAYS"
                        ? "#0284c7"
                        : "#64748b",
                  }}
                >
                  Working Days
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedTypeFilter("SHIFT_HOURS")}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontWeight:
                      selectedTypeFilter === "SHIFT_HOURS" ? 700 : 500,
                    color:
                      selectedTypeFilter === "SHIFT_HOURS"
                        ? "#0284c7"
                        : "#64748b",
                  }}
                >
                  Shift Hours
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedTypeFilter("SLA")}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: selectedTypeFilter === "SLA" ? 700 : 500,
                    color: selectedTypeFilter === "SLA" ? "#0284c7" : "#64748b",
                  }}
                >
                  SLA Coverage
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedTypeFilter("SUPPORT")}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: selectedTypeFilter === "SUPPORT" ? 700 : 500,
                    color:
                      selectedTypeFilter === "SUPPORT" ? "#0284c7" : "#64748b",
                  }}
                >
                  Support
                </button>
              </div>
            )}
        </section>

        {/* Main Data Table */}
        {activeTab === "HOLIDAYS" ? (
          /* =========================================================================
             Holidays Table View
             ========================================================================= */
          <section className="cmts-sod-table-card">
            <table className="cmts-sod-table">
              <thead>
                <tr>
                  <th>Holiday Exception & Name</th>
                  <th>Calendar Scope</th>
                  <th>Holiday Type</th>
                  <th>Region Code</th>
                  <th>Holiday Date</th>
                  <th style={{ textAlign: "center", width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredHolidays.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        textAlign: "center",
                        padding: "3rem",
                        color: "#64748b",
                      }}
                    >
                      <FiCalendar
                        style={{
                          fontSize: "2.5rem",
                          color: "#cbd5e1",
                          display: "block",
                          margin: "0 auto 0.75rem",
                        }}
                      />
                      <strong>
                        No holiday exceptions found for this filter.
                      </strong>
                      <p style={{ margin: "0.35rem 0 0", fontSize: "0.82rem" }}>
                        Add a holiday exception schedule to exclude non-working
                        dates.
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredHolidays.map((row, idx) => (
                    <tr key={String(row.id ?? `hol-${idx}`)}>
                      <td>
                        <div className="cmts-sod-policy-cell">
                          <strong>
                            {String(row.holidayName ?? "Public Holiday")}
                          </strong>
                          <code>
                            {String(row.calendarCode ?? "CAL-STANDARD")}
                          </code>
                        </div>
                      </td>
                      <td>
                        <span className="cmts-sod-badge cmts-sod-badge--domain-config">
                          {String(row.calendarCode ?? "All Calendars")}
                        </span>
                      </td>
                      <td>
                        <span className="cmts-sod-badge cmts-sod-badge--domain-approval">
                          {String(row.holidayType ?? "PUBLIC")}
                        </span>
                      </td>
                      <td>
                        <div
                          style={{
                            fontSize: "0.82rem",
                            color: "#334155",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.35rem",
                          }}
                        >
                          <FiGlobe style={{ color: "#64748b" }} />
                          <span>{String(row.regionCode ?? "National")}</span>
                        </div>
                      </td>
                      <td>
                        <div
                          style={{
                            fontSize: "0.82rem",
                            color: "#334155",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.35rem",
                          }}
                        >
                          <FiClock style={{ color: "#64748b" }} />
                          <span>{String(row.holidayDate ?? "-")}</span>
                        </div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          type="button"
                          className="cmts-notification-actions-button"
                          aria-label="Open holiday actions"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActionsFor({ row, kind: "holiday" });
                          }}
                        >
                          <span
                            className="cmts-notification-actions-button__icon"
                            aria-hidden="true"
                          >
                            ⋮
                          </span>
                          <span>Actions</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>
        ) : activeTab === "SHIFTS" ? (
          /* =========================================================================
             Shifts Table View
             ========================================================================= */
          <section className="cmts-sod-table-card">
            <table className="cmts-sod-table">
              <thead>
                <tr>
                  <th>Shift & Schedule Name</th>
                  <th>Calendar Scope</th>
                  <th>Operational Hours</th>
                  <th>Break Time</th>
                  <th>Active Working Days</th>
                  <th style={{ textAlign: "center", width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredShifts.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        textAlign: "center",
                        padding: "3rem",
                        color: "#64748b",
                      }}
                    >
                      <FiSun
                        style={{
                          fontSize: "2.5rem",
                          color: "#cbd5e1",
                          display: "block",
                          margin: "0 auto 0.75rem",
                        }}
                      />
                      <strong>No working shifts configured.</strong>
                      <p style={{ margin: "0.35rem 0 0", fontSize: "0.82rem" }}>
                        Create shift policies to define flexible working hours.
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredShifts.map((row, idx) => (
                    <tr key={String(row.id ?? `shift-${idx}`)}>
                      <td>
                        <div className="cmts-sod-policy-cell">
                          <strong>
                            {String(row.shiftName ?? "Standard Shift")}
                          </strong>
                          <code>{String(row.shiftCode ?? "SHIFT-01")}</code>
                        </div>
                      </td>
                      <td>
                        <span className="cmts-sod-badge cmts-sod-badge--domain-config">
                          {String(row.calendarCode ?? "CAL-INDIA-STANDARD")}
                        </span>
                      </td>
                      <td>
                        <div
                          style={{
                            fontSize: "0.82rem",
                            color: "#334155",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.35rem",
                          }}
                        >
                          <FiClock style={{ color: "#64748b" }} />
                          <span>
                            {String(row.startTime ?? "09:00")} -{" "}
                            {String(row.endTime ?? "18:00")}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="cmts-sod-badge cmts-sod-badge--result-warn">
                          {String(row.breakMinutes ?? 60)} mins
                        </span>
                      </td>
                      <td>
                        <div className="cmts-sod-constraint-tag">
                          <span>
                            {String(
                              row.workingDayCodes ?? "MON,TUE,WED,THU,FRI",
                            )}
                          </span>
                        </div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          type="button"
                          className="cmts-notification-actions-button"
                          aria-label="Open shift actions"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActionsFor({ row, kind: "shift" });
                          }}
                        >
                          <span
                            className="cmts-notification-actions-button__icon"
                            aria-hidden="true"
                          >
                            ⋮
                          </span>
                          <span>Actions</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>
        ) : activeTab === "REMINDERS" ? (
          /* =========================================================================
             Reminders Table View
             ========================================================================= */
          <section className="cmts-sod-table-card">
            <table className="cmts-sod-table">
              <thead>
                <tr>
                  <th>Reminder Title & Workflow Context</th>
                  <th>SLA Due Date / Target</th>
                  <th>Governance Status</th>
                  <th style={{ textAlign: "center", width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reminders.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        textAlign: "center",
                        padding: "3rem",
                        color: "#64748b",
                      }}
                    >
                      <FiClock
                        style={{
                          fontSize: "2.5rem",
                          color: "#cbd5e1",
                          display: "block",
                          margin: "0 auto 0.75rem",
                        }}
                      />
                      <strong>No operational reminders found.</strong>
                    </td>
                  </tr>
                ) : (
                  reminders.map((rem) => (
                    <tr key={rem.id}>
                      <td>
                        <div className="cmts-sod-policy-cell">
                          <strong>{rem.title}</strong>
                          <code>REM-{rem.id.slice(0, 8)}</code>
                        </div>
                      </td>
                      <td>
                        <div
                          style={{
                            fontSize: "0.82rem",
                            color: "#334155",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.35rem",
                          }}
                        >
                          <FiClock style={{ color: "#64748b" }} />
                          <span>{rem.dueAt ?? "Pending scheduling"}</span>
                        </div>
                      </td>
                      <td>
                        <span className="cmts-sod-badge cmts-sod-badge--status-active">
                          <FiCheckCircle />
                          {rem.status ? rem.status.toUpperCase() : "ACTIVE"}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          type="button"
                          className="cmts-notification-actions-button"
                          onClick={() => {
                            if (props.onOpenLinkedRecord) {
                              props.onOpenLinkedRecord({ id: rem.id });
                            }
                          }}
                        >
                          <FiEye /> View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>
        ) : (
          /* =========================================================================
             Main Calendars Table View (Matching Sample Screenshot)
             ========================================================================= */
          <section className="cmts-sod-table-card">
            <table className="cmts-sod-table">
              <thead>
                <tr>
                  <th>Calendar & Operational Summary</th>
                  <th>Configuration Type</th>
                  <th>Working Hours & Time Zone</th>
                  <th>Lifecycle Status</th>
                  <th>Effective Period</th>
                  <th style={{ textAlign: "center", width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCalendars.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        textAlign: "center",
                        padding: "3rem",
                        color: "#64748b",
                      }}
                    >
                      <FiCalendar
                        style={{
                          fontSize: "2.5rem",
                          color: "#cbd5e1",
                          display: "block",
                          margin: "0 auto 0.75rem",
                        }}
                      />
                      <strong>
                        No calendar configurations matched your criteria.
                      </strong>
                      <p style={{ margin: "0.35rem 0 0", fontSize: "0.82rem" }}>
                        Try adjusting your search filters or click "Create
                        Calendar" to add one.
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredCalendars.map((row) => {
                    const calCode = String(
                      row.calendarCode ?? row.code ?? "CAL-CODE",
                    );
                    const calName = String(
                      row.calendarName ??
                        row.name ??
                        row.title ??
                        "Business Calendar",
                    );
                    const typeLabel = formatCalendarType(
                      row.calendarType,
                      masterCalendarTypeOptions,
                    );
                    const timeZone = String(
                      row.timeZoneId ?? row.timeZone ?? "Asia/Kolkata",
                    );
                    const isDefault = normalizeBoolean(row.isDefault);
                    const isActive =
                      row.isActive !== false &&
                      String(row.status ?? "").toLowerCase() !== "inactive";
                    const effectivePeriod = row.effectiveFrom
                      ? `${String(row.effectiveFrom).slice(0, 10)} → ${row.effectiveTo ? String(row.effectiveTo).slice(0, 10) : "Indefinite"}`
                      : "Permanent";

                    return (
                      <tr key={String(row.id ?? calCode)}>
                        <td>
                          <div className="cmts-sod-policy-cell">
                            <strong>{calName}</strong>
                            <div
                              style={{
                                display: "flex",
                                gap: "0.4rem",
                                alignItems: "center",
                                marginTop: "0.2rem",
                              }}
                            >
                              <code>{calCode}</code>
                              {isDefault && (
                                <span
                                  className="cmts-sod-badge cmts-sod-badge--result-warn"
                                  style={{
                                    fontSize: "0.68rem",
                                    padding: "0.15rem 0.45rem",
                                  }}
                                >
                                  DEFAULT ENGINE
                                </span>
                              )}
                            </div>
                            <span className="cmts-sod-policy-desc">
                              Working Days:{" "}
                              {String(row.workingDaysJson ?? "[1,2,3,4,5]")} |
                              Hours:{" "}
                              {String(
                                row.workingHoursJson ??
                                  '{"start":"09:00","end":"18:00"}',
                              )}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="cmts-sod-badge cmts-sod-badge--domain-config">
                            {typeLabel}
                          </span>
                        </td>
                        <td>
                          <div className="cmts-sod-constraint-tag">
                            <FiGlobe style={{ color: "#0284c7" }} />
                            <span>{timeZone}</span>
                            <FiArrowRight style={{ color: "#94a3b8" }} />
                            <strong>09:00 - 18:00</strong>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`cmts-sod-badge ${isActive ? "cmts-sod-badge--status-active" : "cmts-sod-badge--result-blocked"}`}
                          >
                            {isActive ? <FiCheckCircle /> : <FiSlash />}
                            {isActive ? "ACTIVE" : "INACTIVE"}
                          </span>
                        </td>
                        <td>
                          <div
                            style={{
                              fontSize: "0.82rem",
                              color: "#334155",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.35rem",
                            }}
                          >
                            <FiClock style={{ color: "#64748b" }} />
                            <span>{effectivePeriod}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <button
                            type="button"
                            className="cmts-notification-actions-button"
                            aria-label={`Open lifecycle actions for ${calCode}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              setActionsFor({ row, kind: "calendar" });
                            }}
                          >
                            <span
                              className="cmts-notification-actions-button__icon"
                              aria-hidden="true"
                            >
                              ⋮
                            </span>
                            <span>Actions</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </section>
        )}

        {/* =========================================================================
            Lifecycle Actions Popover Modal (Matching Sample Action Flow)
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={Boolean(actionsFor)}
          title="Calendar Operational Actions"
          subtitle={
            actionsFor
              ? `${String(actionsFor.row.calendarCode ?? actionsFor.row.shiftCode ?? actionsFor.row.code ?? "RECORD")} • ${String(actionsFor.row.calendarName ?? actionsFor.row.shiftName ?? actionsFor.row.holidayName ?? "Item")}`
              : ""
          }
          size="md"
          onClose={() => setActionsFor(null)}
        >
          {actionsFor && actionsFor.kind === "calendar" && (
            <div className="cmts-notification-action-modal">
              {/* Metadata Summary Box */}
              <div className="cmts-notification-action-modal__summary">
                <div>
                  <span>Status</span>
                  <strong
                    style={{
                      color:
                        actionsFor.row.isActive !== false
                          ? "#059669"
                          : "#dc2626",
                    }}
                  >
                    {actionsFor.row.isActive !== false ? "ACTIVE" : "INACTIVE"}
                  </strong>
                </div>

                <div>
                  <span>Type</span>
                  <strong>
                    {formatCalendarType(
                      actionsFor.row.calendarType,
                      masterCalendarTypeOptions,
                    )}
                  </strong>
                </div>

                <div>
                  <span>Time Zone</span>
                  <strong style={{ color: "#334155" }}>
                    {String(
                      actionsFor.row.timeZoneId ??
                        actionsFor.row.timeZone ??
                        "Asia/Kolkata",
                    )}
                  </strong>
                </div>
              </div>

              {/* Action Cards Grid */}
              <div className="cmts-notification-action-modal__grid">
                <ActionCard
                  icon="⚡"
                  title="Test due-date calculation engine"
                  description="Run SLA calculation simulation against this specific calendar profile."
                  onClick={() => {
                    const code = String(
                      actionsFor.row.calendarCode ??
                        actionsFor.row.code ??
                        "CAL-INDIA-STANDARD",
                    );
                    setActionsFor(null);
                    triggerTestDueDate(code);
                  }}
                />

                <ActionCard
                  icon="✏"
                  title="Reschedule / Edit calendar"
                  description="Update operational hours, working days JSON, time zone, or active bounds."
                  onClick={() => {
                    const target = actionsFor.row;
                    setActionsFor(null);
                    openEditModal(target);
                  }}
                />

                <ActionCard
                  icon="👁"
                  title="View full calendar specification"
                  description="Inspect raw configuration JSON, holiday mappings, and shift parameters."
                  onClick={() => {
                    const target = actionsFor.row;
                    setActionsFor(null);
                    openViewModal(target);
                  }}
                />

                <ActionCard
                  icon="🚫"
                  danger
                  title="Deactivate / Cancel calendar"
                  description="Deactivate this calendar profile and unbind from active SLA escalations."
                  disabled={
                    !props.onCancelEvent || actionsFor.row.isActive === false
                  }
                  onClick={async () => {
                    const target = actionsFor.row;
                    setActionsFor(null);
                    try {
                      await requireCmtsUiAction(
                        props.onCancelEvent,
                        "onCancelEvent",
                      )(target);
                      setBannerMessage(
                        `Calendar ${String(target.calendarCode ?? target.code)} has been deactivated.`,
                      );
                    } catch (err) {
                      setLocalError(normalizeUserSafeApiError(err).userMessage);
                    }
                  }}
                />
              </div>
            </div>
          )}

          {actionsFor && actionsFor.kind === "holiday" && (
            <div className="cmts-notification-action-modal">
              <div className="cmts-notification-action-modal__grid">
                <ActionCard
                  icon="✏"
                  title="Edit holiday exception"
                  description="Modify holiday name, date, type, and geographic region scope."
                  onClick={() => {
                    const target = actionsFor.row;
                    setActionsFor(null);
                    openAuxiliary("holiday-edit", target);
                  }}
                />

                <ActionCard
                  icon="🚫"
                  danger
                  title="Deactivate holiday exception"
                  description="Remove this holiday date from calendar exception exclusions."
                  onClick={async () => {
                    const target = actionsFor.row;
                    setActionsFor(null);
                    try {
                      await requireCmtsUiAction(
                        props.onDeactivateHoliday,
                        "onDeactivateHoliday",
                      )(target);
                      setBannerMessage(
                        `Holiday ${String(target.holidayName)} has been removed.`,
                      );
                    } catch (err) {
                      setLocalError(normalizeUserSafeApiError(err).userMessage);
                    }
                  }}
                />
              </div>
            </div>
          )}

          {actionsFor && actionsFor.kind === "shift" && (
            <div className="cmts-notification-action-modal">
              <div className="cmts-notification-action-modal__grid">
                <ActionCard
                  icon="✏"
                  title="Edit working shift"
                  description="Adjust shift operational window, break minutes, and working days."
                  onClick={() => {
                    const target = actionsFor.row;
                    setActionsFor(null);
                    openAuxiliary("shift-edit", target);
                  }}
                />

                <ActionCard
                  icon="🚫"
                  danger
                  title="Deactivate shift schedule"
                  description="Disable this shift policy from active factory / support coverage."
                  onClick={async () => {
                    const target = actionsFor.row;
                    setActionsFor(null);
                    try {
                      await requireCmtsUiAction(
                        props.onDeactivateShift,
                        "onDeactivateShift",
                      )(target);
                      setBannerMessage(
                        `Shift ${String(target.shiftName)} deactivated.`,
                      );
                    } catch (err) {
                      setLocalError(normalizeUserSafeApiError(err).userMessage);
                    }
                  }}
                />
              </div>
            </div>
          )}
        </CmtsEnterpriseModal>

        {/* =========================================================================
            Read-only View Details Modal
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={modalMode === "view"}
          title="Calendar Specification & Details"
          subtitle="View the selected business calendar configuration."
          size="lg"
          onClose={closeModal}
        >
          {selected ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "20px",
                padding: "8px 4px 20px",
              }}
            >
              <div>
                <strong>Calendar Code</strong>
                <div style={{ marginTop: "4px" }}>
                  <code>
                    {String(selected.code ?? selected.calendarCode ?? "-")}
                  </code>
                </div>
              </div>

              <div>
                <strong>Calendar Name</strong>
                <div style={{ marginTop: "4px" }}>
                  {String(selected.name ?? selected.calendarName ?? "-")}
                </div>
              </div>

              <div>
                <strong>Calendar Type</strong>
                <div style={{ marginTop: "4px" }}>
                  {formatCalendarType(
                    selected.calendarType,
                    masterCalendarTypeOptions,
                  )}
                </div>
              </div>

              <div>
                <strong>Time Zone</strong>
                <div style={{ marginTop: "4px" }}>
                  {String(
                    selected.timeZone ?? selected.timeZoneId ?? "Asia/Kolkata",
                  )}
                </div>
              </div>

              <div>
                <strong>Default Engine</strong>
                <div style={{ marginTop: "4px" }}>
                  {normalizeBoolean(selected.isDefault)
                    ? "Yes (Primary)"
                    : "No"}
                </div>
              </div>

              <div>
                <strong>Lifecycle Status</strong>
                <div style={{ marginTop: "4px" }}>
                  {formatBooleanStatus(selected.isActive)}
                </div>
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <strong>Working Days (ISO)</strong>
                <pre
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    padding: "0.75rem",
                    borderRadius: 8,
                    fontSize: "0.85rem",
                    marginTop: "4px",
                  }}
                >
                  {String(selected.workingDaysJson ?? "[1,2,3,4,5]")}
                </pre>
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <strong>Working Hours Rule (JSON)</strong>
                <pre
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    padding: "0.75rem",
                    borderRadius: 8,
                    fontSize: "0.85rem",
                    marginTop: "4px",
                  }}
                >
                  {String(
                    selected.workingHoursJson ??
                      '{"start":"09:00","end":"18:00"}',
                  )}
                </pre>
              </div>

              <div
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "12px",
                }}
              >
                <button
                  type="button"
                  className="cmts-sod-btn cmts-sod-btn--secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          ) : null}
        </CmtsEnterpriseModal>

        {/* =========================================================================
            Sleek Modern Create / Update Calendar Modal (Image 2 style)
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={modalMode === "create" || modalMode === "edit"}
          title={
            modalMode === "edit"
              ? `Edit Calendar: ${calendarForm.code || "Profile"}`
              : "Create Business Calendar"
          }
          subtitle="Configure enterprise operating days, hours, and SLA evaluation parameters."
          size="lg"
          onClose={closeModal}
          footer={
            <div className="cmts-job-modal-footer">
              <button
                type="button"
                className="cmts-job-modal-btn cmts-job-modal-btn--secondary"
                disabled={props.busy}
                onClick={closeModal}
              >
                Cancel
              </button>

              <button
                type="button"
                className="cmts-job-modal-btn cmts-job-modal-btn--primary"
                disabled={props.busy}
                onClick={handleSaveCalendar}
              >
                {props.busy
                  ? "Saving..."
                  : modalMode === "edit"
                    ? "Save Changes"
                    : "Create Calendar"}
              </button>
            </div>
          }
        >
          <div className="cmts-job-form">
            {localError && (
              <div
                style={{
                  padding: "0.65rem 0.9rem",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 7,
                  color: "#991b1b",
                  fontSize: "0.8rem",
                }}
              >
                {localError}
              </div>
            )}

            <div className="cmts-job-form__grid">
              <label>
                Calendar Code *
                <input
                  type="text"
                  value={calendarForm.code}
                  disabled={modalMode === "edit"}
                  placeholder="e.g. CAL-INDIA-STANDARD"
                  onChange={(e) =>
                    setCalendarForm((prev) => ({
                      ...prev,
                      code: e.target.value.toUpperCase().replaceAll(" ", "_"),
                    }))
                  }
                />
                <small>
                  Unique uppercase identifier for backend engine binding
                </small>
              </label>

              <label>
                Calendar Name *
                <input
                  type="text"
                  value={calendarForm.name}
                  placeholder="e.g. India Standard Business Calendar"
                  onChange={(e) =>
                    setCalendarForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
                <small>
                  Human readable title displayed in reports & workflows
                </small>
              </label>

              <label>
                Calendar Type *
                <select
                  value={calendarForm.calendarType}
                  onChange={(e) =>
                    setCalendarForm((prev) => ({
                      ...prev,
                      calendarType: e.target.value,
                    }))
                  }
                >
                  {CALENDAR_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <small>Category of operational schedule policy</small>
              </label>

              <label>
                Time Zone *
                <select
                  value={calendarForm.timeZone}
                  onChange={(e) =>
                    setCalendarForm((prev) => ({
                      ...prev,
                      timeZone: e.target.value,
                    }))
                  }
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
                <small>Regional timezone for SLA due-date calculation</small>
              </label>

              <label>
                Effective From *
                <input
                  type="date"
                  value={calendarForm.effectiveFrom}
                  onChange={(e) =>
                    setCalendarForm((prev) => ({
                      ...prev,
                      effectiveFrom: e.target.value,
                    }))
                  }
                />
                <small>Start date of calendar policy validity</small>
              </label>

              <label>
                Effective To
                <input
                  type="date"
                  value={calendarForm.effectiveTo}
                  onChange={(e) =>
                    setCalendarForm((prev) => ({
                      ...prev,
                      effectiveTo: e.target.value,
                    }))
                  }
                />
                <small>
                  Optional expiration date (leave empty for indefinite)
                </small>
              </label>

              {/* Working Hours Pickers */}
              <label>
                Shift Start Time *
                <input
                  type="time"
                  value={calendarForm.startTime}
                  onChange={(e) =>
                    setCalendarForm((prev) => ({
                      ...prev,
                      startTime: e.target.value,
                    }))
                  }
                />
                <small>Daily operating start window (e.g. 09:00)</small>
              </label>

              <label>
                Shift End Time *
                <input
                  type="time"
                  value={calendarForm.endTime}
                  onChange={(e) =>
                    setCalendarForm((prev) => ({
                      ...prev,
                      endTime: e.target.value,
                    }))
                  }
                />
                <small>Daily operating close window (e.g. 18:00)</small>
              </label>

              {/* Day-Pill Selector */}
              <div
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.35rem",
                }}
              >
                <label
                  style={{
                    color: "#334155",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                  }}
                >
                  Active Working Days *
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                    marginTop: "0.2rem",
                  }}
                >
                  {DAYS_OF_WEEK.map((d) => {
                    const isSelected = calendarForm.workingDays.includes(d.day);
                    return (
                      <button
                        key={d.day}
                        type="button"
                        onClick={() => {
                          setCalendarForm((prev) => ({
                            ...prev,
                            workingDays: isSelected
                              ? prev.workingDays.filter((x) => x !== d.day)
                              : [...prev.workingDays, d.day].sort(
                                  (a, b) => a - b,
                                ),
                          }));
                        }}
                        style={{
                          padding: "0.45rem 0.9rem",
                          borderRadius: 7,
                          border: isSelected
                            ? "1.5px solid #0284c7"
                            : "1px solid #cbd5e1",
                          background: isSelected ? "#e0f2fe" : "#ffffff",
                          color: isSelected ? "#0369a1" : "#475569",
                          fontWeight: isSelected ? 700 : 500,
                          fontSize: "0.8rem",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.35rem",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {isSelected && (
                          <FiCheck style={{ fontSize: "0.85rem" }} />
                        )}
                        <span>{d.label}</span>
                      </button>
                    );
                  })}
                </div>
                <small
                  style={{
                    color: "#94a3b8",
                    fontSize: "0.65rem",
                    marginTop: "0.15rem",
                  }}
                >
                  Selected:{" "}
                  {calendarForm.workingDays
                    .map((d) => DAYS_OF_WEEK.find((x) => x.day === d)?.label)
                    .filter(Boolean)
                    .join(", ") || "None"}
                </small>
              </div>
            </div>

            {/* Default & Active Toggles */}
            <label className="cmts-job-form__toggle">
              <input
                type="checkbox"
                checked={calendarForm.isDefault}
                onChange={(e) =>
                  setCalendarForm((prev) => ({
                    ...prev,
                    isDefault: e.target.checked,
                  }))
                }
              />
              <span>
                <strong>Set as Default Calendar</strong>
                <small>
                  Use this calendar profile as primary engine for tenant SLA and
                  reminder schedules.
                </small>
              </span>
            </label>

            <label className="cmts-job-form__toggle">
              <input
                type="checkbox"
                checked={calendarForm.isActive}
                onChange={(e) =>
                  setCalendarForm((prev) => ({
                    ...prev,
                    isActive: e.target.checked,
                  }))
                }
              />
              <span>
                <strong>Enable Calendar Profile</strong>
                <small>
                  Active calendars are automatically evaluated for complaint
                  milestone and SLA resolution targets.
                </small>
              </span>
            </label>
          </div>
        </CmtsEnterpriseModal>

        {/* =========================================================================
            Modern Holiday & Shift Form Modals
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={auxiliaryMode?.startsWith("holiday") ?? false}
          title={
            auxiliaryMode === "holiday-edit"
              ? "Edit Holiday Exception"
              : "Add Holiday Exception"
          }
          subtitle="Configure holiday exception date to exclude from SLA business day calculation."
          size="lg"
          onClose={closeAuxiliary}
          footer={
            <div className="cmts-job-modal-footer">
              <button
                type="button"
                className="cmts-job-modal-btn cmts-job-modal-btn--secondary"
                disabled={props.busy}
                onClick={closeAuxiliary}
              >
                Cancel
              </button>

              <button
                type="button"
                className="cmts-job-modal-btn cmts-job-modal-btn--primary"
                disabled={props.busy}
                onClick={handleSaveHoliday}
              >
                {props.busy
                  ? "Saving..."
                  : auxiliaryMode === "holiday-edit"
                    ? "Save Changes"
                    : "Create Holiday"}
              </button>
            </div>
          }
        >
          <div className="cmts-job-form">
            {localError && (
              <div
                style={{
                  padding: "0.65rem 0.9rem",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 7,
                  color: "#991b1b",
                  fontSize: "0.8rem",
                }}
              >
                {localError}
              </div>
            )}

            <div className="cmts-job-form__grid">
              <label>
                Calendar Scope *
                <select
                  value={holidayForm.calendarCode}
                  onChange={(e) =>
                    setHolidayForm((prev) => ({
                      ...prev,
                      calendarCode: e.target.value,
                    }))
                  }
                >
                  {calendarRows.map((c, i) => {
                    const code = String(c.calendarCode ?? c.code ?? `CAL-${i}`);
                    return (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    );
                  })}
                </select>
                <small>Calendar profile to bind this holiday exception</small>
              </label>

              <label>
                Holiday Date *
                <input
                  type="date"
                  value={holidayForm.holidayDate}
                  onChange={(e) =>
                    setHolidayForm((prev) => ({
                      ...prev,
                      holidayDate: e.target.value,
                    }))
                  }
                />
                <small>Specific exception date</small>
              </label>

              <label>
                Holiday Name *
                <input
                  type="text"
                  value={holidayForm.holidayName}
                  placeholder="e.g. Independence Day"
                  onChange={(e) =>
                    setHolidayForm((prev) => ({
                      ...prev,
                      holidayName: e.target.value,
                    }))
                  }
                />
                <small>Title of the public holiday or shutdown</small>
              </label>

              <label>
                Holiday Type *
                <select
                  value={holidayForm.holidayType}
                  onChange={(e) =>
                    setHolidayForm((prev) => ({
                      ...prev,
                      holidayType: e.target.value,
                    }))
                  }
                >
                  <option value="PUBLIC">Public Holiday</option>
                  <option value="NATIONAL">National Holiday</option>
                  <option value="REGIONAL">Regional Holiday</option>
                  <option value="COMPANY">Company Shutdown</option>
                </select>
                <small>Classification of exception</small>
              </label>

              <label style={{ gridColumn: "1 / -1" }}>
                Region / Territory Code
                <input
                  type="text"
                  value={holidayForm.regionCode}
                  placeholder="e.g. IN-NATIONAL, IN-TN, EU-ALL"
                  onChange={(e) =>
                    setHolidayForm((prev) => ({
                      ...prev,
                      regionCode: e.target.value,
                    }))
                  }
                />
                <small>Optional territorial scope identifier</small>
              </label>
            </div>
          </div>
        </CmtsEnterpriseModal>

        <CmtsEnterpriseModal
          open={auxiliaryMode?.startsWith("shift") ?? false}
          title={
            auxiliaryMode === "shift-edit"
              ? "Edit Working Shift"
              : "Create Working Shift"
          }
          subtitle="Configure operational shift hours, break allocations, and active days."
          size="lg"
          onClose={closeAuxiliary}
          footer={
            <div className="cmts-job-modal-footer">
              <button
                type="button"
                className="cmts-job-modal-btn cmts-job-modal-btn--secondary"
                disabled={props.busy}
                onClick={closeAuxiliary}
              >
                Cancel
              </button>

              <button
                type="button"
                className="cmts-job-modal-btn cmts-job-modal-btn--primary"
                disabled={props.busy}
                onClick={handleSaveShift}
              >
                {props.busy
                  ? "Saving..."
                  : auxiliaryMode === "shift-edit"
                    ? "Save Changes"
                    : "Create Shift"}
              </button>
            </div>
          }
        >
          <div className="cmts-job-form">
            {localError && (
              <div
                style={{
                  padding: "0.65rem 0.9rem",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 7,
                  color: "#991b1b",
                  fontSize: "0.8rem",
                }}
              >
                {localError}
              </div>
            )}

            <div className="cmts-job-form__grid">
              <label>
                Calendar Scope *
                <select
                  value={shiftForm.calendarCode}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      calendarCode: e.target.value,
                    }))
                  }
                >
                  {calendarRows.map((c, i) => {
                    const code = String(c.calendarCode ?? c.code ?? `CAL-${i}`);
                    return (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    );
                  })}
                </select>
                <small>Calendar profile to bind this shift policy</small>
              </label>

              <label>
                Shift Code *
                <input
                  type="text"
                  value={shiftForm.shiftCode}
                  disabled={auxiliaryMode === "shift-edit"}
                  placeholder="e.g. SHIFT-MORNING"
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      shiftCode: e.target.value
                        .toUpperCase()
                        .replaceAll(" ", "_"),
                    }))
                  }
                />
                <small>Unique shift identifier code</small>
              </label>

              <label>
                Shift Name *
                <input
                  type="text"
                  value={shiftForm.shiftName}
                  placeholder="e.g. Morning General Shift"
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      shiftName: e.target.value,
                    }))
                  }
                />
                <small>Descriptive shift title</small>
              </label>

              <label>
                Break Allocation (Minutes) *
                <input
                  type="number"
                  min="0"
                  max="240"
                  value={shiftForm.breakMinutes}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      breakMinutes: Number(e.target.value),
                    }))
                  }
                />
                <small>Total break duration excluded from working SLA</small>
              </label>

              <label>
                Start Time *
                <input
                  type="time"
                  value={shiftForm.startTime}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      startTime: e.target.value,
                    }))
                  }
                />
                <small>Shift starts at</small>
              </label>

              <label>
                End Time *
                <input
                  type="time"
                  value={shiftForm.endTime}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      endTime: e.target.value,
                    }))
                  }
                />
                <small>Shift ends at</small>
              </label>

              {/* Working Days Selector for Shifts */}
              <div
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.35rem",
                }}
              >
                <label
                  style={{
                    color: "#334155",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                  }}
                >
                  Active Shift Days *
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                    marginTop: "0.2rem",
                  }}
                >
                  {DAYS_OF_WEEK.map((d) => {
                    const isSelected = shiftForm.workingDays.includes(d.code);
                    return (
                      <button
                        key={d.code}
                        type="button"
                        onClick={() => {
                          setShiftForm((prev) => ({
                            ...prev,
                            workingDays: isSelected
                              ? prev.workingDays.filter((x) => x !== d.code)
                              : [...prev.workingDays, d.code],
                          }));
                        }}
                        style={{
                          padding: "0.45rem 0.9rem",
                          borderRadius: 7,
                          border: isSelected
                            ? "1.5px solid #0284c7"
                            : "1px solid #cbd5e1",
                          background: isSelected ? "#e0f2fe" : "#ffffff",
                          color: isSelected ? "#0369a1" : "#475569",
                          fontWeight: isSelected ? 700 : 500,
                          fontSize: "0.8rem",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.35rem",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {isSelected && (
                          <FiCheck style={{ fontSize: "0.85rem" }} />
                        )}
                        <span>{d.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </CmtsEnterpriseModal>

        {/* =========================================================================
            Due-Date Calculation Simulation Modal
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={testDueDateModalOpen}
          title="SLA Due-Date Engine Simulation"
          subtitle="Test real-time due-date computation against backend calendar rules and shifts."
          size="lg"
          onClose={() => setTestDueDateModalOpen(false)}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              padding: "0.5rem 0",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: "#334155",
                    marginBottom: "0.35rem",
                  }}
                >
                  Select Calendar Profile
                </label>
                <select
                  value={testCalendarCode}
                  onChange={(e) => setTestCalendarCode(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.75rem",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    fontSize: "0.875rem",
                    background: "#fff",
                  }}
                >
                  {calendarRows.map((c, i) => {
                    const code = String(c.calendarCode ?? c.code ?? `CAL-${i}`);
                    const name = String(c.calendarName ?? c.name ?? code);
                    return (
                      <option key={code} value={code}>
                        {code} - {name}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: "#334155",
                    marginBottom: "0.35rem",
                  }}
                >
                  Timezone Context
                </label>
                <input
                  type="text"
                  value={testTimezone}
                  onChange={(e) => setTestTimezone(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.75rem",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    fontSize: "0.875rem",
                  }}
                />
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: "#334155",
                    marginBottom: "0.35rem",
                  }}
                >
                  Start Date & Time (ISO)
                </label>
                <input
                  type="datetime-local"
                  value={testStartAt}
                  onChange={(e) => setTestStartAt(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.75rem",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    fontSize: "0.875rem",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: "#334155",
                    marginBottom: "0.35rem",
                  }}
                >
                  SLA Duration ({testDurationUnit})
                </label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    type="number"
                    value={testDuration}
                    onChange={(e) => setTestDuration(Number(e.target.value))}
                    style={{
                      flex: 1,
                      padding: "0.6rem 0.75rem",
                      borderRadius: 8,
                      border: "1px solid #cbd5e1",
                      fontSize: "0.875rem",
                    }}
                  />
                  <select
                    value={testDurationUnit}
                    onChange={(e) => setTestDurationUnit(e.target.value)}
                    style={{
                      padding: "0.6rem 0.75rem",
                      borderRadius: 8,
                      border: "1px solid #cbd5e1",
                      fontSize: "0.875rem",
                      background: "#fff",
                    }}
                  >
                    <option value="Minutes">Minutes</option>
                    <option value="Hours">Hours</option>
                    <option value="Days">Days</option>
                  </select>
                </div>
              </div>
            </div>

            {testResult && (
              <div
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 8,
                  padding: "1rem",
                  color: "#166534",
                  fontSize: "0.875rem",
                  marginTop: "0.5rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontWeight: 600,
                  }}
                >
                  <FiCheckCircle />
                  <span>Calculation Engine Simulation Result</span>
                </div>
                <div
                  style={{
                    marginTop: "0.5rem",
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                    background: "#fff",
                    padding: "0.5rem",
                    borderRadius: 6,
                    border: "1px solid #dcfce7",
                  }}
                >
                  {testResult}
                </div>
              </div>
            )}

            {localError && (
              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 8,
                  padding: "0.75rem 1rem",
                  color: "#991b1b",
                  fontSize: "0.85rem",
                }}
              >
                {localError}
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.75rem",
                marginTop: "1rem",
              }}
            >
              <button
                type="button"
                className="cmts-sod-btn cmts-sod-btn--secondary"
                onClick={() => setTestDueDateModalOpen(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="cmts-sod-btn cmts-sod-btn--primary"
                onClick={handleExecuteTestDueDate}
                disabled={props.busy}
              >
                <FiZap /> Calculate Due Date
              </button>
            </div>
          </div>
        </CmtsEnterpriseModal>
      </div>
    </PageContainer>
  );
}
